import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import CommentSection from 'app/components/layouts/Comments/CommentSection';
import RightContent from 'app/components/layouts/MenuRight/RightContent';
import { faVolumeUp, faHeart } from "@fortawesome/free-solid-svg-icons";
import FontAwesomeIcon from "app/components/layouts/Icons/FontAwesome";
import GoogleAdsense from 'app/components/layouts/Ads/GoogleAdsense';
import { useEffect, useState, useMemo, useRef } from 'react';
import { withGlobalData } from 'app/utils/withGlobalData';
import { createUserActivity } from 'app/requests/commons';
import { vocabularyUrlSlug } from 'app/utils/string';
import * as listenApi from 'app/requests/listens';
import List from 'app/components/listening/List';
import Title from 'app/components/layouts/Title';
import { useTranslation } from 'next-i18next';
import { Translate } from 'types/commons';
import { useRouter } from 'next/router';
import { Listen } from 'types/listen';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import env from '.env.json';

import dynamic from "next/dynamic";
const BarSound = dynamic(() => import("app/components/layouts/BarSound/Index"), { ssr: false });
import 'app/components/listening/styles.css';

interface ListenDetailProps {
  listen: Listen;
  nextListen: [];
}

export default function ListenDetail({ listen, nextListen }: ListenDetailProps) {
  if (!listen) return <></>

  const [visible, setVisible] = useState(false);
  const [isLoop, setIsLoop] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const { t } = useTranslation();
  const audioPlayer = useRef<any>(undefined);
  const router = useRouter();
  const code = router?.locale == 'en' ? 'vi' : router?.locale!;

  useEffect(() => {
    setCurrentTime(0);
    setIsLoop(false)
    createUserActivity({
      'activity_type': 'listens',
      'activity_id': listen._id,
    })
  }, [router.asPath]);

  const setPositionTime = (time: number) => {
    audioPlayer.current.currentTime = time;
    audioPlayer.current.play();
  }

  const onPlaying = () => {
    setCurrentTime(audioPlayer.current.currentTime);
  };

  const toggleAutoReplay = () => {
    audioPlayer.current.loop = !isLoop;
    setIsLoop(!isLoop)
  }

  const title = useMemo(() => {
    return listen.translates.find((translate: Translate) => translate.type == 'title') as Translate;
  }, [listen]);

  const description = useMemo(() => {
    return listen.translates.find((translate: Translate) => translate.type == 'description') as Translate;
  }, [listen]);

  const lyrics = useMemo(() => {
    return listen.translates.filter((translate: Translate) => translate.type == 'lyric');
  }, [listen]);

  const conversation = useMemo(() => {
    return lyrics.map((item: Translate, index: number) => {
      const user = index % 2 === 0 ? listen.users[0] : listen.users[1];
      const time = JSON.parse(item.startTime || '');
      const isCurrentText = 0 < currentTime && time[0] <= currentTime && currentTime < time[1];

      return <div key={index}>
        <div className="play_audio_part mt-2 d-flex">
          <div className={`person ${isCurrentText ? 'text-info font-weight-bold' : ''}`}><strong>{user}: </strong></div>
          <div className="text_speak">
            <span
              onClick={() => setPositionTime(time[0])}
              className={`d-flex ${isCurrentText ? 'text-info font-weight-bold' : ''} cursor-pointer text-ta`}
            >
              {item.en}
              {!isCurrentText &&
                <FontAwesomeIcon className="text-size-md ml-2 mt-1" icon={faVolumeUp} />
              }
              {isCurrentText &&
                <BarSound />
              }
            </span>
            <small className={`text-size-sm ${isCurrentText ? 'text-info' : ''}`}>
              <i className={`text-translate ${visible ? 'show' : ''}`}>{item[code]}</i>
            </small>
          </div>
        </div>
      </div>
    })
  }, [lyrics, currentTime, visible])

  return (
    <section className="box-card-area">
      <NextSeo
        title={listen.title}
        description={listen.title + ' ' + title[code]}
      />
      <div className="container-md">
        <div className="d-flex">
          <div className="box-news-area">
            <div className='content_inner'>
              <Title title={listen.title}/>
              <div className='text-center'>
                  <small className="text-capitalize text-size-md text-center">
                    {title[code]}
                  </small>
              </div>
              <div className="form-group text-center mt-2">
                <audio
                  ref={audioPlayer}
                  key={listen.slug}
                  onTimeUpdate={onPlaying}
                  onEnded={() => setCurrentTime(0)}
                  onPause={() => setCurrentTime(0)}
                  style={{ minWidth: '70%' }}
                  controlsList="nodownload"
                  className="mt-2" controls
                >
                  <source src={`${env.URL_API_FILE || env.URL_API_NODE}/listen/${listen.url}`} />
                </audio>
                <div className="d-flex justify-content-center mt-2">
                  <button onClick={() => setVisible(!visible)} className="btn btn-info mr-2">
                    {visible ? t('listening.turn_off_translate') : t('listening.turn_on_translate')}
                  </button>
                  <button onClick={() => toggleAutoReplay()} className="btn btn-success">
                    {isLoop ? t('listening.turn_off_auto_replay') : t('listening.turn_on_auto_replay')}
                  </button>
                </div>
              </div>
              <div className="form-group mt-2">
                {conversation}

                <div className="mt-4">
                  <h2 className="text-info text-size-2xl">{t('listening.vocabulary')}</h2>
                  {
                    listen?.vocabularies?.map((vocabulary: string, index: number) => {
                      return (
                        <Link className="pr-2" key={index} href={`/vocabularies/${vocabularyUrlSlug(vocabulary, router?.locale)}`}>
                          <em className='link-primary'>{vocabulary}</em>
                        </Link>
                      )
                    })
                  }
                </div>

                <div className="mt-4 mb-2 summary">
                  <h2 className="text-info text-size-2xl">{t('listening.summary')}</h2>
                  <i className="text-size-sm" dangerouslySetInnerHTML={{ __html: description[code] }}></i>
                </div>
              </div>
              <GoogleAdsense width="100%" dataSlot="7482232136" />
              <div className="note">
                <FontAwesomeIcon className="text-danger mr-2" icon={faHeart} />
                <span dangerouslySetInnerHTML={{ __html: t('listening.hope', { title: listen.title }) }} />
              </div>
              <div className="note mt-3">
                <h2 className="text-info text-size-2xl">
                  {t('listening.nextListen')}
                </h2>
                <div className="mt-4">
                  <List
                    listens={nextListen}
                    code={code}
                  />
                </div>
              </div>
            </div>
            <CommentSection roomId={`listen-id-${listen?.id}`} />
          </div>
          <RightContent />
        </div>
      </div>
    </section>
  )
}

export const getServerSideProps = withGlobalData(async ({ locale, query }) => {
  const redirectToListening = {
    redirect: {
      destination: '/listening',
      permanent: false,
    },
  };

  try {
    const [data, translations] = await Promise.all([
      listenApi.getByListenSlug(query.slug, locale),
      serverSideTranslations(locale, ['common', 'listen-detail']),
    ]);

    const listen = data.listen;
    const nextListen = data.nextListen;

    if (!listen) {
      return redirectToListening;
    }

    return {
      props: {
        ...translations,
        listen,
        nextListen
      }
    };
  } catch (error) {
    console.error('Error in getServerSideProps:', error);
    return redirectToListening;
  }
});
